// =============================================================================
// Origin UI — src/pages/admin/CostCentreAdminTab.tsx
// Admin Config tab for managing Cost Centre reference data.
// PrePrd Data Import — Phase B.19.
// =============================================================================

import { useState, useEffect } from 'react';
import { api } from '@/services/apiService';
import { Card, ErrorBanner, LoadingSpinner } from '@/components/common';
import styles from './admin.module.css';

interface CostCentre {
  costCentreId: number;
  code: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
}

export function CostCentreAdminTab() {
  const [items, setItems] = useState<CostCentre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ code: '', description: '', isActive: true, sortOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => { load(); }, [showInactive]);

  async function load() {
    try {
      setLoading(true);
      const data = await api.get<CostCentre[]>(`/api/cost-centres?includeInactive=${showInactive}`);
      setItems(data);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }

  function startAdd() {
    setForm({ code: '', description: '', isActive: true, sortOrder: 0 });
    setAdding(true);
    setEditingId(null);
  }

  function startEdit(item: CostCentre) {
    setForm({ code: item.code, description: item.description || '', isActive: item.isActive, sortOrder: item.sortOrder });
    setEditingId(item.costCentreId);
    setAdding(false);
  }

  async function handleSave() {
    if (!form.code.trim()) { setError('Code is required'); return; }
    try {
      setSaving(true); setError('');
      if (editingId) {
        await api.put(`/api/cost-centres/${editingId}`, {
          code: form.code.trim(),
          description: form.description.trim() || null,
          isActive: form.isActive,
          sortOrder: form.sortOrder,
        });
      } else {
        await api.post('/api/cost-centres', {
          code: form.code.trim(),
          description: form.description.trim() || null,
        });
      }
      setAdding(false); setEditingId(null);
      await load();
    } catch (err: any) { setError(err.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function handleDeactivate(id: number) {
    try {
      setError('');
      await api.delete(`/api/cost-centres/${id}`);
      await load();
    } catch (err: any) { setError(err.message); }
  }

  if (loading) return <LoadingSpinner message="Loading cost centres..." />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 className={styles.sectionTitle} style={{ margin: 0 }}>Cost Centres ({items.length})</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ fontSize: '13px', color: '#4A5568', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Show inactive
            </label>
            <button className={styles.addBtn} onClick={startAdd}>+ Add Cost Centre</button>
          </div>
        </div>

        {(adding || editingId) && (
          <div style={{ background: '#F7FAFC', padding: '16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #E2E8F0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: '4px' }}>Code *</label>
                <input
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #CBD5E0', borderRadius: '6px', fontSize: '14px' }}
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. IT - DUBAI"
                />
              </div>
              <div>
                <label style={{ fontSize: '13px', fontWeight: 600, color: '#2D3748', display: 'block', marginBottom: '4px' }}>Description</label>
                <input
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #CBD5E0', borderRadius: '6px', fontSize: '14px' }}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
            </div>
            {editingId && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '12px', alignItems: 'center' }}>
                <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                  Active
                </label>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#2D3748', marginRight: '4px' }}>Sort Order:</label>
                  <input
                    type="number"
                    style={{ width: '60px', padding: '4px 8px', border: '1px solid #CBD5E0', borderRadius: '4px', fontSize: '13px' }}
                    value={form.sortOrder}
                    onChange={e => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
              <button className={styles.cancelBtn} onClick={() => { setAdding(false); setEditingId(null); }}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: '#4A5568', fontWeight: 600 }}>Code</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: '#4A5568', fontWeight: 600 }}>Description</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', color: '#4A5568', fontWeight: 600 }}>Status</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: '#4A5568', fontWeight: 600 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.costCentreId} style={{ borderBottom: '1px solid #EDF2F7', opacity: item.isActive ? 1 : 0.5 }}>
                <td style={{ padding: '10px 12px', fontWeight: 500 }}>{item.code}</td>
                <td style={{ padding: '10px 12px', color: '#718096' }}>{item.description || '—'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                    background: item.isActive ? '#C6F6D5' : '#FED7D7',
                    color: item.isActive ? '#22543D' : '#9B2C2C',
                  }}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    <button onClick={() => startEdit(item)} style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid #CBD5E0', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>Edit</button>
                    {item.isActive && (
                      <button onClick={() => handleDeactivate(item.costCentreId)} style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid #FEB2B2', borderRadius: '4px', background: 'white', color: '#E53E3E', cursor: 'pointer' }}>Deactivate</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#A0AEC0' }}>No cost centres configured yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
